import L from 'leaflet';
import shp from 'shpjs';
import spainBeachArea from '../../../assets/data/spainBeachArea.zip';
import spainCityArea from '../../../assets/data/spainCityArea.zip';
import { Feature, Geometry } from 'geojson';

interface GeoJSONFeature {
  type: string;
  geometry: {
    type: string;
    coordinates: any;
  };
  properties?: Record<string, any>;
}

interface GeoJSON {
  type: string;
  features: GeoJSONFeature[];
}

class SpainControl extends L.Control {
  private map: L.Map | null = null;
  private spainBeachLayer: L.FeatureGroup | null = null;
  private spainCityLayer: L.FeatureGroup | null = null;
  private onAreaSelected: ((e: { layer: L.Layer }) => void) | null = null;

  constructor(options?: L.ControlOptions & { onAreaSelected?: (e: { layer: L.Layer }) => void }) {
    super(options);
    this.onAreaSelected = options?.onAreaSelected || null;
  }

  onAdd(map: L.Map) {
    this.map = map;

    // Create the container for the legend
    const div = L.DomUtil.create('div', 'spain-control-legend');
    div.style.position = 'absolute';
    div.style.bottom = '10px';
    div.style.left = '10px';
    div.style.backgroundColor = 'white';
    div.style.padding = '10px';
    div.style.borderRadius = '8px';
    div.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
    div.style.width = '220px';
    div.style.zIndex = '1000';
    div.innerHTML = `
      <h4>Select Layer</h4>
      <div>
        <label>
          <input type="radio" name="spain-layer" value="none" checked />
          None
        </label>
      </div>
      <div>
        <label>
          <input type="radio" name="spain-layer" value="beach" />
          AMB Beach Area
        </label>
      </div>
      <div>
        <label>
          <input type="radio" name="spain-layer" value="city" />
          AMB City Area
        </label>
      </div>
    `;

    // Add event listeners to handle layer selection
    const inputs = div.querySelectorAll('input[name="spain-layer"]');
    inputs.forEach((input) => {
      input.addEventListener('change', (event: Event) => {
        const target = event.target as HTMLInputElement;
        this.handleLayerSelection(target.value);
      });
    });

    return div;
  }

  onRemove() {
    if (this.map) {
      // Remove all layers when the control is removed
      if (this.spainBeachLayer) {
        this.map.removeLayer(this.spainBeachLayer);
      }
      if (this.spainCityLayer) {
        this.map.removeLayer(this.spainCityLayer);
      }
    }
    this.map = null;
  }

  private removeThirdValueFromGeoJSON(geojson: GeoJSON): GeoJSON {
    return {
      ...geojson,
      features: geojson.features.map((feature) => ({
        ...feature,
        geometry: {
          ...feature.geometry,
          coordinates: Array.isArray(feature.geometry.coordinates)
            ? feature.geometry.coordinates.map((coordArray: any[]) =>
              Array.isArray(coordArray)
                ? coordArray.map((coord: [number, number, number]) =>
                  Array.isArray(coord) ? coord.slice(0, 2) : coord,
                )
                : coordArray,
            )
            : feature.geometry.coordinates,
        },
      })),
    };
  }

  private async loadSpainBeachLayer(): Promise<void> {
    if (!this.map) return;

    // Remove the other layer if active
    if (this.spainCityLayer) {
      this.map.removeLayer(this.spainCityLayer);
    }
    if (!this.spainBeachLayer) {
      const geojsonData = (await shp(spainBeachArea)) as GeoJSON | GeoJSON[];
      let processedGeoJSON: GeoJSON;
      if (Array.isArray(geojsonData)) {
        processedGeoJSON = {
          type: 'FeatureCollection',
          features: geojsonData.flatMap((item) => item.features),
        };
      } else {
        processedGeoJSON = geojsonData;
      }
      processedGeoJSON = this.removeThirdValueFromGeoJSON(processedGeoJSON);
      // Create the Beach Area layer group
      this.spainBeachLayer = L.featureGroup();
      L.geoJSON(
        {
          type: 'FeatureCollection',
          features: processedGeoJSON.features,
        } as GeoJSON.FeatureCollection,
        {
          style: {
            color: 'blue',
            weight: 2,
            opacity: 0.5,
            fillOpacity: 0.2,
          },
          onEachFeature: (feature: Feature<Geometry, any>, layer: L.Layer) => {
            layer.bindPopup(`<b>${feature.properties?.Name || 'No Name'}</b>`);

            // Add click event to create polygon
            layer.on('click', () => {
              this.createPolygonFromFeature(layer);
            });
          },
        },
      ).addTo(this.spainBeachLayer);
    }

    // Add the Beach Area layer to the map
    this.spainBeachLayer.addTo(this.map);

    // Fly to the area
    const bounds = this.spainBeachLayer.getBounds();
    if (bounds.isValid()) {
      this.map.flyToBounds(bounds);
    }
  }

  private async loadSpainCityLayer() {
    if (!this.map) return;

    // Remove the beach layer if active
    if (this.spainBeachLayer) {
      this.map.removeLayer(this.spainBeachLayer);
    }

    if (!this.spainCityLayer) {
      const geojsonData = await shp(spainCityArea);
      const features = Array.isArray(geojsonData) ? geojsonData.flatMap((item) => item.features) : geojsonData.features;
      // Create the City Area layer group
      this.spainCityLayer = L.featureGroup();
      L.geoJSON(features, {
        style: {
          color: 'green',
          weight: 2,
          opacity: 0.5,
          fillOpacity: 0.2,
        },
        onEachFeature: (feature, layer) => {
          layer.bindPopup(`<b>${feature.properties?.NOMMUNI || 'No Name'}</b>`);
          // Add click event to create polygon
          layer.on('click', () => {
            // Type assertion for GeoJSON layer
            const geoJsonLayer = layer as L.GeoJSON;
            if (!geoJsonLayer.feature) {
              console.error('Layer has no feature data');
              return;
            }

            const feature = geoJsonLayer.feature as GeoJSON.Feature<GeoJSON.Geometry>;
            const geometry = feature.geometry;

            if (geometry.type === 'MultiPolygon') {
              // Type-safe MultiPolygon handling
              const multiCoords = geometry.coordinates;
              let maxPoints = -1;
              let selectedPolygon: GeoJSON.Position[][] | null = null;

              // Find the polygon with the most points (including all rings)
              multiCoords.forEach((polygon: GeoJSON.Position[][]) => {
                const totalPoints = polygon.reduce((sum, ring) => sum + ring.length, 0);
                if (totalPoints > maxPoints) {
                  maxPoints = totalPoints;
                  selectedPolygon = polygon;
                }
              });

              if (!selectedPolygon) {
                console.error('No polygon found in MultiPolygon');
                return;
              }

              // Create new Polygon feature with proper typing
              const newGeometry: GeoJSON.Polygon = {
                type: 'Polygon',
                coordinates: selectedPolygon,
              };

              const newFeature: GeoJSON.Feature<GeoJSON.Polygon> = {
                ...feature,
                geometry: newGeometry,
              };

              // Create temporary layer and extract the polygon
              const tempLayer = L.geoJSON(newFeature);
              const convertedLayer = tempLayer.getLayers()[0];

              if (!convertedLayer) {
                console.error('Failed to convert feature to layer');
                return;
              }

              this.createPolygonFromFeature(convertedLayer);
            } else if (geometry.type === 'Polygon') {
              // Directly use the original layer if it's a Polygon
              this.createPolygonFromFeature(layer);
            } else {
              console.warn(`Unsupported geometry type: ${geometry.type}`);
            }
          });
        },
      }).addTo(this.spainCityLayer);
    }

    // Add the City Area layer to the map
    this.spainCityLayer.addTo(this.map);

    // Fly to the area
    const bounds = this.spainCityLayer.getBounds();
    if (bounds.isValid()) {
      this.map.flyToBounds(bounds);
    }
  }

  private createPolygonFromFeature(layer: L.Layer) {
    if (!this.map) return;
    // Ensure the layer has a toGeoJSON method
    if (layer && 'toGeoJSON' in layer) {
      // Trigger the onAreaSelected callback if provided
      if (this.onAreaSelected) {
        this.onAreaSelected({ layer }); // Wrap in an object to match expected structure
      }
    }
  }

  private handleLayerSelection(layer: string) {
    switch (layer) {
      case 'beach':
        this.loadSpainBeachLayer();
        break;
      case 'city':
        this.loadSpainCityLayer();
        break;
      case 'none':
        this.removeAllLayers();
        break;
    }
  }

  private removeAllLayers() {
    if (this.map) {
      if (this.spainBeachLayer) {
        this.map.removeLayer(this.spainBeachLayer);
      }
      if (this.spainCityLayer) {
        this.map.removeLayer(this.spainCityLayer);
      }
    }
  }
}

export default SpainControl;
